import $ from 'jquery';
import _ from 'underscore';
import Environment from '../environment';
import Eventable from '../eventable';
import { getLogger } from '../logging/logger';

const logger = getLogger('AudioManager');

/**
 * @typedef {Object} PriorityAudio
 * @param {string} priority A priority value to evaluate if 1 sound should cancel
 * 		another in a single audio encoder environment.
 *
 * @typedef {HTMLAudioElement | PriorityAudio} HwpTvAudio
 */

export const SOUNDS = Object.freeze({
	ballotPick: 'ballotPick',
	bracketPick: 'bracketPick',
	buttonChange: 'buttonChange',
	buttonSelect: 'buttonSelect',
	connectFourDrop: 'connectFourDrop',
	connectFourLose: 'connectFourLose',
	connectFourTie: 'connectFourTie',
	connectFourWin: 'connectFourWin',
	crosswordBonus: 'crosswordBonus',
	crosswordCorrect: 'crosswordCorrect',
	crosswordIncorrect: 'crosswordIncorrect',
	dxCorrect: 'dxCorrect',
	dxIncorrect: 'dxIncorrect',
	dxTimerExpired: 'dx-timer-expired',
	hubAchievementActivated: 'ding',
	hubArcadeChange: 'hubChangeOption',
	hubArcadeLaunch: 'hubLaunchArcade',
	hubRoomChange: 'hubChangeOption',
	hubLegalChange: 'hubChangeOption',
	lockboxFail: 'lockboxFail',
	lockboxSuccess: 'lockboxSuccess',
	lockboxTryUnlock: 'lockboxTryUnlock',
	mmMatch: 'mmMatch',
	mmMiss: 'mmMiss',
	pathNodeSelected: 'pathNodeSelected',
	pathStart: 'pathStart',
	sceneStreakIncorrectChoice: 'sceneStreakIncorrectChoice',
	sceneStreakOverlay: 'sceneStreakOverlay',
	sceneStreakPointsReward: 'sceneStreakPointsReward',
	slideFailed: 'slideFailed',
	slidePuzzleComplete: 'slidePuzzleComplete',
	slidePuzzleTimeExpired: 'slidePuzzleTimeExpired',
	timerExpired: 'timerExpired',
	timerLow: 'timeLow',
	tilePopNotAPop: 'tilePopNotAPop',
	tilePoppedBoardEmpty: 'tilePoppedBoardEmpty',
	tilePopPopped: 'tilePopPopped',
	triviaCorrect: 'triviaCorrect',
	triviaIncorrect: 'triviaIncorrect',
	wellConnectedCorrect: 'wellConnectedCorrect',
	wellConnectedIncorrect: 'wellConnectedIncorrect',
	wellConnectedTimeExpired: 'wellConnectedTimeExpired',
	wordSearchCorrect: 'wordSearchCorrect',
	wordSearchFoundAll: 'wordSearchFoundAll',
	wordSearchIncorrect: 'wordSearchIncorrect',
});

export const EVENTS = Object.freeze({
	initialized: 'initialized',
});

class AudioManager extends Eventable {
	constructor(options) {
		logger.log('[initialize]', { options });
		super();

		this.contentUrl = '/hwpHub/json/audioData.json';
		this.tracks = [];
		this.data = [];
		/** @type {HwpTvAudio} */
		this.currentAudio = new Audio();
		this.activeSounds = [];
		this.muted = false;

		if (this.supportsMultipleTracks()) {
			this.backgroundAudio = new Audio();
			this.backgroundAudio.loop = true;
		}

		this.initialize(options);
	}

	initialize(options) {
		logger.log('[Initialize]', { options });

		Object.assign(this, options);

		return fetch(this.contentUrl)
			.then((response) => response.json())
			.then(({ data = [] }) => {
				this.data = data;
				this.initialized = true;
				this.trigger(EVENTS.initialized);
			});
	}

	playSound(soundId) {
		logger.log('[playSound]', soundId);

		if (this.muted) {
			return Promise.resolve();
		}

		if (!soundId) {
			logger.warn('Missing soundId');
			return Promise.reject();
		}

		if (this.isVideoPlaying()) {
			logger.warn('[return because video is playing]');
			return Promise.reject();
		}

		const sndFile = this.data.find(({ id }) => id === soundId);
		if (!sndFile) {
			logger.warn('[Unknown Audio]', { soundId });
			return Promise.reject();
		}

		// Get source url for audio or exit
		const { priority = 1 } = sndFile;
		let { src } = sndFile;
		if (Array.isArray(src)) {
			src = _.sample(src);
		}
		if (!src) {
			logger.log('Invalid sound source url', { soundId });
			return Promise.resolve();
		}
		const currentAudio = this.getAudioObject();

		if (
			currentAudio.src &&
			currentAudio.priority !== undefined &&
			currentAudio.priority > priority
		) {
			logger.warn(`${soundId} has a lower priority than what's playing:`, {
				[`${soundId}Priority`]: priority,
				currentAudioSrc: currentAudio.src,
				currentAudioPriority: currentAudio.priority,
			});
			return Promise.resolve();
		}

		// Clean up any existing audio
		if (!currentAudio.paused) {
			currentAudio.pause();
		}

		if (this.activeSounds && this.activeSounds.indexOf(currentAudio) < 0) {
			this.activeSounds.push(currentAudio);
		}

		currentAudio.src = src;
		currentAudio.priority = priority;
		currentAudio.load();
		currentAudio.play();

		return new Promise((resolve) => {
			$(currentAudio).one('ended', () => {
				if (this.activeSounds && this.activeSounds.indexOf(currentAudio) >= 0) {
					const soundIndex = this.activeSounds.indexOf(currentAudio);
					this.activeSounds.splice(soundIndex, 1);
					// Reset current audio once it's ended
					this.currentAudio.priority = null;
					this.currentAudio.src = '';
					this.currentAudio.load();
				}
				resolve();
			});
		});
	}

	/**
	 * Returns an audio instance to use. In environments multiple audio is supported a new Audio
	 * object will be returned. In environments where only 1 sound is sound is supported a single Audio
	 * object will be returned.
	 * @returns {HwpTvAudio}
	 */
	getAudioObject() {
		logger.log('[getAudioObject]');
		if (this.supportsMultipleTracks()) {
			return new Audio();
		}

		if (!this.currentAudio) {
			this.currentAudio = new Audio();
			this.currentAudio.priority = 1;
		}

		return this.currentAudio;
	}

	playBackgroundMusic(soundId) {
		logger.log('[PlayBackgroundMusic]');

		if (!this.supportsMultipleTracks()) {
			return Promise.reject();
		}

		if (!soundId) {
			logger.warn('Missing soundId');
			return Promise.reject(new Error('Missing soundId'));
		}

		if (this.isVideoPlaying()) {
			logger.warn('return because video is playing');
			return Promise.reject(new Error('Video in progress'));
		}

		const sndFile = this.data.find(({ id }) => id === soundId);
		if (sndFile) {
			this.backgroundAudio.src = sndFile.src;
			this.backgroundAudio.load();
			this.backgroundAudio.play();
		} else {
			return Promise.reject(new Error('Missing Sound definition'));
		}

		return Promise.resolve();
	}

	resumeBackgroundMusic() {
		if (this.backgroundAudio && this.backgroundAudio.paused) {
			this.backgroundAudio.play();
		}
	}

	stopAllSounds() {
		logger.log('[stopAllSounds]');
		if (this.currentAudio) {
			this.currentAudio.pause();
			this.currentAudio.src = '';
		}
	}

	pauseBackgroundMusic() {
		if (this.backgroundAudio) {
			this.backgroundAudio.pause();
		}
	}

	stopAllAudio() {
		this.stopAllSounds();
		this.pauseBackgroundMusic();
	}

	isVideoPlaying() {
		logger.log('[isVideoPlaying]');

		return $('video')
			.toArray()
			.some((/** @type {HTMLVideoElement} */ vid) => vid.currentTime > 0 && !vid.paused);
	}

	supportsMultipleTracks() {
		logger.log('[supportsMultipleTracks]');
		return !Environment.isSetTopBox();
	}
}

export default new AudioManager();
